import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { KeyboardArrowLeft } from '@mui/icons-material';
import axios from '../utils/axios';
import {
  FORM_TYPE_LABELS,
  formatCurrency as fc,
  attachSquareCard,
  newIdempotencyKey
} from '../utils/campusMinistryFields';

const CARD_CONTAINER_ID = 'cm-payment-card-container';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
  } catch {
    return '';
  }
};

// Balance payment page for Campus Ministry signups. Mirrors OCIAPaymentLookup:
// look up by email (current occurrence of this form only), pick a
// registration, pay any part of the remaining balance.
const CampusMinistryPaymentLookup = () => {
  const { formType } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const formLabel = FORM_TYPE_LABELS[formType] || 'Campus Ministry';

  const [step, setStep] = useState('lookup');
  const [email, setEmail] = useState('');
  const [looking, setLooking] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [selectedReg, setSelectedReg] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [squareReady, setSquareReady] = useState(false);
  const [squareCard, setSquareCard] = useState(null);
  // One Square idempotency key per payment attempt, kept across retries after
  // an ambiguous failure so the same payment can never be charged twice.
  const paymentIdemKeyRef = useRef(null);

  useEffect(() => {
    document.title = `${formLabel} Payment`;
  }, [formLabel]);

  useEffect(() => {
    if (step !== 'pay') return;
    if (squareReady) return;

    let cancelled = false;
    const init = async () => {
      try {
        const card = await attachSquareCard(axios, CARD_CONTAINER_ID);
        if (cancelled) {
          try { await card.destroy(); } catch (_) { /* noop */ }
          return;
        }
        setSquareCard(card);
        setSquareReady(true);
      } catch (e) {
        console.error('Square init failed:', e);
        setError('The payment system could not be loaded. Please refresh the page and try again.');
      }
    };
    init();
    return () => { cancelled = true; };
  }, [step, squareReady]);

  const destroyCard = () => {
    if (squareCard) {
      try { squareCard.destroy(); } catch (_) { /* noop */ }
    }
    setSquareCard(null);
    setSquareReady(false);
  };

  const handleLookup = async () => {
    setError(null);
    setSuccess(null);
    const trimmedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLooking(true);
    try {
      const res = await axios.get(`/campus-ministry/lookup/${encodeURIComponent(trimmedEmail)}`, { params: { formType } });
      const results = Array.isArray(res.data) ? res.data : [];
      if (results.length === 0) {
        setError(`No ${formLabel} registration was found for ${trimmedEmail}.`);
        return;
      }
      setRegistrations(results);
      setSelectedReg(results.length === 1 ? results[0] : null);
      setStep('select');
    } catch (e) {
      if (e?.response?.status === 404) {
        setError(`No ${formLabel} registration was found for ${trimmedEmail}. Please use the email you registered with.`);
      } else {
        setError(e?.response?.data?.message || 'Lookup failed. Please try again.');
      }
      setRegistrations([]);
    } finally {
      setLooking(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!selectedReg) {
      setError('Please select a registration.');
      return;
    }
    if (selectedReg.remainingBalance <= 0) {
      setError('This registration has no remaining balance.');
      return;
    }
    setError(null);
    setPaymentAmount(selectedReg.remainingBalance.toFixed(2));
    setStep('pay');
  };

  const handleBackToLookup = () => {
    setRegistrations([]);
    setSelectedReg(null);
    setError(null);
    setSuccess(null);
    setStep('lookup');
  };

  const handleBackToSelect = () => {
    destroyCard();
    setError(null);
    setSuccess(null);
    setStep('select');
  };

  const handleProcessPayment = async () => {
    setError(null);
    setSuccess(null);
    const amount = Math.round(parseFloat(paymentAmount) * 100) / 100;
    if (!amount || amount < 1) {
      setError('Please enter an amount of at least $1.00.');
      return;
    }
    if (amount > selectedReg.remainingBalance + 0.001) {
      setError(`The amount cannot be more than your remaining balance of ${fc(selectedReg.remainingBalance)}.`);
      return;
    }
    if (!squareCard) {
      setError('The payment form is not ready yet. Please wait a moment and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const tokenResult = await squareCard.tokenize();
      if (tokenResult.status !== 'OK') {
        const msg = (tokenResult.errors || []).map(e => e.message).join(', ') || 'Please check your card details.';
        throw new Error(msg);
      }

      if (!paymentIdemKeyRef.current) paymentIdemKeyRef.current = newIdempotencyKey();
      const res = await axios.post(`/campus-ministry/payment/${selectedReg.id}`, {
        paymentAmount: amount,
        squarePaymentToken: tokenResult.token,
        paymentIdempotencyKey: paymentIdemKeyRef.current
      });
      // Payment settled — the next payment is a new attempt with a new key.
      paymentIdemKeyRef.current = null;

      if (res.data?.pendingManualReview) {
        setSuccess(res.data.message);
        destroyCard();
        setStep('select');
        return;
      }

      const newBalance = Number(res.data?.newBalance) || 0;
      setSuccess(`Thank you! Your payment of ${fc(amount)} was received. Remaining balance: ${fc(newBalance)}.`);

      const applyUpdate = (r) => ({
        ...r,
        totalPaid: (r.totalPaid || 0) + amount,
        remainingBalance: newBalance,
        paymentStatus: res.data?.paymentStatus || r.paymentStatus,
        transactions: [
          ...(r.transactions || []),
          { amount, date: new Date().toISOString(), method: 'online', reference: res.data?.paymentId }
        ]
      });
      setSelectedReg(prev => prev ? applyUpdate(prev) : prev);
      setRegistrations(prev => prev.map(r => r.id === selectedReg.id ? applyUpdate(r) : r));
      setPaymentAmount(newBalance > 0 ? newBalance.toFixed(2) : '');

      if (newBalance <= 0) {
        destroyCard();
        setStep('select');
      }
    } catch (e) {
      // 400/402 mean no charge happened — safe to use a fresh key on retry.
      // Anything else is ambiguous: keep the key so a retry can't double-charge.
      const status = e?.response?.status;
      if (status === 400 || status === 402) {
        paymentIdemKeyRef.current = null;
      }
      setError(e?.response?.data?.message || e?.message || 'Payment failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderLookupStep = () => (
    <Box>
      <Typography variant="h5" gutterBottom>{formLabel} Payment</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter the email address you registered with to see your balance and make a payment.
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !looking) handleLookup(); }}
          fullWidth
          autoComplete="email"
        />
        <Button
          variant="contained"
          onClick={handleLookup}
          disabled={looking || !email.trim()}
          size={isMobile ? 'large' : 'medium'}
          fullWidth={isMobile}
          sx={{ minHeight: isMobile ? 48 : undefined }}
        >
          {looking ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Look Up Registration'}
        </Button>
      </Stack>
    </Box>
  );

  const renderRegistrationCard = (reg) => {
    const selected = selectedReg && selectedReg.id === reg.id;
    const paid = reg.remainingBalance <= 0;
    return (
      <Paper
        key={reg.id}
        elevation={selected ? 3 : 1}
        onClick={() => setSelectedReg(reg)}
        sx={{
          p: 2,
          cursor: 'pointer',
          border: '2px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'action.hover' : 'background.paper',
          transition: 'all 0.15s ease'
        }}
      >
        <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{reg.name}</Typography>
            <Typography variant="body2" color="text.secondary">{reg.eventName}</Typography>
          </Box>
          <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
            <Typography variant="body2">Total: {fc(reg.totalAmount)}</Typography>
            <Typography variant="body2">Paid: {fc(reg.totalPaid)}</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: paid ? 'success.main' : 'error.main' }}>
              {paid ? 'Paid in full' : `Balance: ${fc(reg.remainingBalance)}`}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const renderSelectStep = () => (
    <Box>
      <Button onClick={handleBackToLookup} startIcon={<KeyboardArrowLeft />} sx={{ mb: 2 }}>
        Look up a different email
      </Button>
      <Typography variant="h6" gutterBottom>
        {registrations.length === 1 ? 'Your Registration' : 'Select a Registration'}
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {registrations.map(renderRegistrationCard)}
      </Stack>

      {selectedReg && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Payment History</Typography>
          {selectedReg.transactions && selectedReg.transactions.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Method</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedReg.transactions
                    .slice()
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((tx, idx) => (
                      <TableRow key={`${tx.reference || idx}-${idx}`}>
                        <TableCell>{formatDate(tx.date)}</TableCell>
                        <TableCell>{fc(tx.amount)}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{tx.method || '—'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">No payments yet.</Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Button
            variant="contained"
            color="primary"
            disabled={selectedReg.remainingBalance <= 0}
            onClick={handleProceedToPayment}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
            sx={{ minHeight: isMobile ? 48 : undefined }}
          >
            {selectedReg.remainingBalance <= 0 ? 'Paid in Full' : `Pay Balance (${fc(selectedReg.remainingBalance)})`}
          </Button>
        </Paper>
      )}
    </Box>
  );

  const renderPayStep = () => (
    <Box>
      <Button onClick={handleBackToSelect} startIcon={<KeyboardArrowLeft />} sx={{ mb: 2 }}>
        Back to registration
      </Button>
      <Typography variant="h6" gutterBottom>Make a Payment</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{selectedReg.name}</Typography>
        <Typography variant="body2">{selectedReg.eventName}</Typography>
        <Typography variant="body2">Total: {fc(selectedReg.totalAmount)}</Typography>
        <Typography variant="body2">Paid: {fc(selectedReg.totalPaid)}</Typography>
        <Typography variant="subtitle2" color="error" sx={{ fontWeight: 700 }}>
          Balance due: {fc(selectedReg.remainingBalance)}
        </Typography>
      </Paper>

      <TextField
        label="Payment Amount"
        type="number"
        value={paymentAmount}
        onChange={(e) => setPaymentAmount(e.target.value)}
        fullWidth
        inputProps={{ min: 1, max: selectedReg.remainingBalance, step: '0.01' }}
        helperText="You can pay any amount toward your balance."
        sx={{ mb: 2 }}
      />

      <Box id={CARD_CONTAINER_ID} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: 60, mb: 2 }} />

      {!squareReady && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading secure payment form…</Typography>
        </Box>
      )}

      <Button
        variant="contained"
        color="success"
        disabled={!squareReady || submitting}
        onClick={handleProcessPayment}
        fullWidth
        size={isMobile ? 'large' : 'medium'}
        sx={{ minHeight: isMobile ? 48 : undefined }}
      >
        {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : `Pay ${fc(parseFloat(paymentAmount || 0))}`}
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      <Paper sx={{ p: isMobile ? 2 : 4 }}>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
        {step === 'lookup' && renderLookupStep()}
        {step === 'select' && renderSelectStep()}
        {step === 'pay' && renderPayStep()}
      </Paper>
    </Container>
  );
};

export default CampusMinistryPaymentLookup;
